import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Heart, X, Users, ShieldCheck, MapPin } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("dating");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/matches/discover?mode=${m}`);
      setCandidates(data.candidates || []);
      setIdx(0);
    } catch {
      toast.error("Could not load introductions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(mode); }, [mode]);

  const act = async (action) => {
    const cand = candidates[idx];
    if (!cand) return;
    try {
      const { data } = await api.post("/matches/action", {
        target_user_id: cand.user.user_id,
        mode,
        action,
      });
      if (data.matched) {
        toast.success(`A mutual choice with ${cand.user.name}.`);
      }
      setIdx((i) => i + 1);
    } catch {
      toast.error("Action failed.");
    }
  };

  const current = candidates[idx];

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F5F5F5]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-12">
        {!user?.verified && (
          <div className="mb-8 goji-glass p-6 flex items-center justify-between" data-testid="dashboard-verify-banner">
            <div className="flex items-center gap-4">
              <ShieldCheck className="w-5 h-5 text-[#D4AF37]" />
              <div>
                <p className="font-display text-base text-[#F5F5F5]">You are not yet verified.</p>
                <p className="text-xs text-[#F5F5F5]/55 mt-1">Complete verification to fully enter the circle.</p>
              </div>
            </div>
            <button onClick={() => navigate("/verify")} className="btn-ghost !py-2 !px-5" data-testid="dashboard-verify-button">
              Verify
            </button>
          </div>
        )}

        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <span className="font-label text-[0.65rem] text-[#D4AF37]">Today</span>
            <h1 className="font-display text-4xl sm:text-5xl font-normal mt-3 text-[#F5F5F5]" data-testid="dashboard-title">
              Considered <span className="italic text-[#D4AF37]">introductions.</span>
            </h1>
          </div>

          <div className="goji-glass flex" data-testid="dashboard-mode-toggle">
            <button
              onClick={() => setMode("dating")}
              className={`px-5 py-3 font-label text-[0.65rem] transition-colors ${
                mode === "dating" ? "bg-[#D4AF37]/10 text-[#D4AF37] border-r border-[#D4AF37]/30" : "text-[#F5F5F5]/45 hover:text-[#F5F5F5]/80 border-r border-[#D4AF37]/15"
              }`}
              data-testid="dashboard-mode-dating"
            >
              <Heart className="inline w-3 h-3 mr-1.5 -mt-0.5" /> Romance
            </button>
            <button
              onClick={() => setMode("fam")}
              className={`px-5 py-3 font-label text-[0.65rem] transition-colors ${
                mode === "fam" ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#F5F5F5]/45 hover:text-[#F5F5F5]/80"
              }`}
              data-testid="dashboard-mode-fam"
            >
              <Users className="inline w-3 h-3 mr-1.5 -mt-0.5" /> Chosen Family
            </button>
          </div>
        </div>

        {loading ? (
          <div className="goji-glass p-16 text-center" data-testid="dashboard-loading">
            <p className="font-display text-2xl italic text-[#D4AF37]">Selecting your introductions...</p>
            <p className="mt-3 text-sm text-[#F5F5F5]/55">A considered intelligence is reading bios and weighing presence.</p>
          </div>
        ) : !current ? (
          <div className="goji-glass p-16 text-center" data-testid="dashboard-empty">
            <p className="font-display text-3xl italic text-[#F5F5F5]">That is all for today.</p>
            <p className="text-[#F5F5F5]/55 mt-2">Fresh introductions arrive tomorrow.</p>
            <button onClick={() => load(mode)} className="mt-8 btn-ghost" data-testid="dashboard-reload-button">Refresh</button>
          </div>
        ) : (
          <div className="goji-glass-strong overflow-hidden" data-testid="dashboard-card">
            <div className="relative">
              <img
                src={current.user.photo_url || "https://placehold.co/600x600/1A1A1A/D4AF37?text=Goji"}
                alt={current.user.name}
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/85 to-transparent p-7">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-display text-4xl font-normal text-[#F5F5F5]" data-testid="dashboard-card-name">{current.user.name}</h2>
                  {current.user.age && <span className="font-display text-3xl text-[#F5F5F5]/70">, {current.user.age}</span>}
                  {current.user.verified && (
                    <span className="px-2.5 py-1 border border-[#D4AF37]/50 font-label text-[0.55rem] text-[#D4AF37] flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                {current.user.location && (
                  <p className="text-sm text-[#F5F5F5]/60 flex items-center gap-1.5 mt-2 font-label text-[0.65rem]">
                    <MapPin className="w-3 h-3" /> {current.user.location}
                  </p>
                )}
              </div>
              <div className="absolute top-4 right-4 bg-[#1A1A1A]/85 border border-[#D4AF37]/40 px-3 py-1.5">
                <span className="font-label text-[0.6rem] text-[#D4AF37]">Resonance</span>
                <span className="ml-2 font-display text-lg text-[#F5F5F5]">{current.score}</span>
              </div>
            </div>

            <div className="p-7">
              <p className="text-[#F5F5F5]/85 leading-relaxed italic" data-testid="dashboard-card-bio">"{current.user.bio}"</p>
              {current.reason && (
                <div className="mt-5 p-5 border border-[#D4AF37]/25">
                  <p className="font-label text-[0.6rem] text-[#D4AF37] mb-2">Why you might resonate</p>
                  <p className="text-sm text-[#F5F5F5]/80 italic">{current.reason}</p>
                </div>
              )}
              {current.user.interests?.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {current.user.interests.slice(0, 8).map((i) => (
                    <span key={i} className="px-3 py-1 border border-[#D4AF37]/25 font-label text-[0.6rem] text-[#F5F5F5]/75">
                      {i}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="p-7 border-t border-[#D4AF37]/15 flex justify-center gap-8">
              <button
                onClick={() => act("pass")}
                className="w-14 h-14 border border-[#D4AF37]/30 hover:border-[#F5F5F5]/60 flex items-center justify-center transition-colors"
                data-testid="dashboard-pass-button"
                aria-label="Pass"
              >
                <X className="w-5 h-5 text-[#F5F5F5]/60" />
              </button>
              <button
                onClick={() => act("like")}
                className="w-14 h-14 border border-[#D4AF37] bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 flex items-center justify-center transition-colors"
                data-testid="dashboard-like-button"
                aria-label="Choose"
              >
                <Heart className="w-5 h-5 text-[#D4AF37]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
